import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popover from '@mui/material/Popover';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OperatorType } from './OperatorChip.tsx';
import { getChipContainerStyles, getOperatorChipStyles } from './utils/getChipStyles.ts';
import { getOperatorText } from './utils/getOperatorText.ts';

type OperatorDropdownChipBaseProps = {
  operator: Exclude<OperatorType, typeof OperatorType.EXCLUDE>;
  andOperatorDisabled?: boolean;
  dataTest?: string;
};

type OperatorDropdownChipReadOnlyProps = OperatorDropdownChipBaseProps & {
  readOnly: true;
  onOperatorChange?: never;
};

type OperatorDropdownChipEditableProps = OperatorDropdownChipBaseProps & {
  readOnly?: false;
  onOperatorChange: (next: OperatorType) => void;
};

type OperatorDropdownChipDynamicProps = OperatorDropdownChipBaseProps & {
  readOnly: boolean;
  onOperatorChange: (next: OperatorType) => void;
};

type OperatorDropdownChipProps =
  | OperatorDropdownChipReadOnlyProps
  | OperatorDropdownChipEditableProps
  | OperatorDropdownChipDynamicProps;

export const OperatorDropdownChip = (props: OperatorDropdownChipProps) => {
  const { operator, andOperatorDisabled = false } = props;
  const readOnly = props.readOnly === true;
  const onOperatorChange = props.readOnly ? undefined : props.onOperatorChange;
  const dataTest = props.dataTest ?? 'group-operator-dropdown';
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (readOnly) {
        return;
      }

      setAnchorEl(event.currentTarget);
    },
    [readOnly]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (next: OperatorType) => {
      if (next === OperatorType.AND && andOperatorDisabled) {
        return;
      }

      handleClose();

      if (readOnly) {
        return;
      }

      onOperatorChange?.(next);
    },
    [handleClose, andOperatorDisabled, readOnly, onOperatorChange]
  );

  const displayText = getOperatorText(operator, t);

  return (
    <Box sx={getChipContainerStyles(true)}>
      <Button
        data-test={dataTest}
        variant="outlined"
        onClick={handleClick}
        disabled={readOnly}
        sx={{
          ...getOperatorChipStyles(operator, true),
          '& .MuiButton-endIcon': {
            marginLeft: 0.5,
          },
          ...(readOnly && {
            pointerEvents: 'none',
          }),
        }}
        endIcon={<ExpandMoreIcon sx={{ fontSize: '14px !important' }} />}
      >
        {displayText}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
            },
          },
        }}
      >
        <MenuList>
          <MenuItem
            sx={{
              width: 90,
              ...(andOperatorDisabled && {
                color: 'text.disabled',
                opacity: theme => theme.palette.action.disabledOpacity,
              }),
            }}
            selected={operator === OperatorType.AND}
            aria-disabled={andOperatorDisabled || undefined}
            data-test="and-item"
            onClick={() => handleSelect(OperatorType.AND)}
          >
            {t('And')}
          </MenuItem>

          <MenuItem
            sx={{ width: 90 }}
            selected={operator === OperatorType.OR}
            data-test="or-item"
            onClick={() => handleSelect(OperatorType.OR)}
          >
            {t('Or')}
          </MenuItem>
        </MenuList>
      </Popover>
    </Box>
  );
};
